import React, { CSSProperties, useEffect, useState } from 'react';
import classNames from 'classnames';
import { cn } from '../lib/utils';
import { CheckerIcon } from './bs-icons';
import { SquareChecker } from './square-checker';

export interface LineCheckerProps {
  defaultChecked?: boolean;
  checked?: boolean;
  disabled?: boolean;
  onCheckedChange?: (checked: boolean) => void;

  icon?: React.ReactNode;
  title: string;
  shortDesc?: string;
  className?: string;
  extra?: React.ReactNode;

  style?: CSSProperties;
  streaming?: boolean;
  confirmed?: boolean;
  readonly?: boolean;
}

export const LineChecker: React.FC<LineCheckerProps> = ({
  defaultChecked = false,
  checked: checkedProp,
  disabled = false,
  onCheckedChange,
  icon,
  className,
  title,
  extra,
  style,
  streaming,
  confirmed,
  readonly,
  shortDesc,
}) => {
  const [checked, setChecked] = useState(defaultChecked);

  useEffect(() => {
    if (checkedProp !== undefined) {
      setChecked(checkedProp);
    }
  }, [checkedProp]);

  const handleClick = () => {
    if (streaming || disabled) return;

    const newChecked = !checked;
    setChecked(newChecked);
    onCheckedChange?.(newChecked);
  };

  const hasExtra = Boolean(extra);

  const checker =
    confirmed && checked ? (
      <CheckerIcon className="grow-0 shrink-0 text-[#0265ff]" />
    ) : (
      <SquareChecker
        className={classNames('outline-none grow-0 shrink-0 cursor-pointer', {
          'mt-[2px]': hasExtra,
          'opacity-80 cursor-not-allowed': disabled,
          'opacity-50': streaming,
          'pointer-events-none': streaming,
        })}
        id={title}
        checked={checked}
        disabled={disabled || confirmed}
        onCheckedChange={handleClick}
      />
    );

  return (
    <div
      className={cn(
        'flex items-center gap-[10px] px-4 py-[10px] rounded-[4px]',
        'w-full transition-colors cursor-pointer',
        {
          'cursor-not-allowed': disabled,
          'hover:bg-[#EFF0F6]': !disabled,
          'items-start': hasExtra,
          'items-center': !hasExtra,
          'pointer-events-none': streaming || disabled || readonly || confirmed,
        },
        className
      )}
      style={style}
      onClick={() => {
        handleClick();
      }}
    >
      {readonly ? null : checker}

      <div
        className={classNames('flex flex-col flex-1 min-w-0', {
          'gap-2.5': hasExtra,
        })}
      >
        <div
          className={classNames('flex items-center justify-between w-full', {
            'gap-1.5': !!icon,
            'gap-2.5': !icon,
          })}
        >
          <div
            className={classNames('flex items-center gap-[6px] min-w-0', {
              'flex-1': !!icon,
              'w-[100px] flex-shrink-0': !icon,
            })}
          >
            {/* Icon */}
            {icon && (
              <div className="w-4 h-4 flex-shrink-0 text-[#6F829E] leading-[16px]">{icon}</div>
            )}

            {/* Title - truncates */}
            <p
              className={classNames(' min-w-0 text-sm text-[#121111] truncate text-[12px]', {
                'flex-1': !!icon,
                'flex-grow-1 flex-shrink-0': !icon,
                '!text-[#666]': readonly,
              })}
            >
              {title}
            </p>
          </div>

          {shortDesc && (
            <p
              className={classNames('text-sm text-[#666] text-[12px]', {
                'whitespace-nowrap flex-shrink-0': !!icon,
                anywhere: !icon,
                '!text-[#121111]': readonly,
              })}
            >
              {shortDesc}
            </p>
          )}
        </div>
        {hasExtra ? (
          <div
            className={cn(
              'overflow-x-auto whitespace-nowrap no-scrollbar flex items-center gap-2',
              {
                '!overflow-x-hidden': disabled || readonly || confirmed,
              }
            )}
          >
            {extra}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default LineChecker;
